<script lang="ts" setup>
import { Component as TComponent, markRaw, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { DialogSystem } from './DialogSystem';
import { cancelQueuedDialogsForId, enqueueDialogShow, type DialogQueueOptions } from './dialogQueue';

type DialogInfo = {
  component: TComponent;
  props: Record<string, any>;
  isInDom: boolean;
  element: any;
};

const dialogs = ref(new Map<string, DialogInfo>());

function storeRef(element: any, dialog: DialogInfo): void {
  dialog.element = element;
}

const addDialog = (id: string, component: TComponent, props: Record<string, any> = {}): void => {
  if (dialogs.value.has(id)) {
    console.error(`Dialog with id "${id}" already exists.`);
    return;
  }
  dialogs.value.set(id, {
    component: markRaw(component),
    props: markRaw(props),
    isInDom: false,
    element: null,
  });
};

const removeDialog = (id: string): void => {
  if (!dialogs.value.has(id)) {
    console.error(`Dialog with id "${id}" does not exist.`);
    return;
  }
  cancelQueuedDialogsForId(id);
  dialogs.value.delete(id);
};

const show = async (id: string, params: any[], markShown?: () => void): Promise<unknown> => {
  const dialog = dialogs.value.get(id);
  if (!dialog) {
    console.error(`Dialog with id "${id}" does not exist.`);
    return;
  }
  dialog.isInDom = true;
  await nextTick();
  if (dialog.element && typeof dialog.element.show === 'function') {
    const result = dialog.element.show(...params);
    await nextTick();
    markShown?.();
    return result;
  }

  console.error(`Dialog component for id "${id}" does not have a show method.`);
};

const showQueued = async (id: string, params: any[], options?: DialogQueueOptions): Promise<unknown> => {
  return enqueueDialogShow({
    id,
    options,
    run: (markShown) => show(id, params, markShown),
  });
};

const hide = async (id: string): Promise<void> => {
  const dialog = dialogs.value.get(id);
  if (!dialog) {
    console.error(`Dialog with id "${id}" does not exist.`);
    return;
  }

  if (dialog.element && typeof dialog.element.hide === 'function') {
    dialog.element.hide();
  } else {
    console.error(`Dialog component for id "${id}" does not have a hide method.`);
  }
};

onMounted(() => {
  DialogSystem.addDialog = addDialog;
  DialogSystem.removeDialog = removeDialog;
  DialogSystem.show = show;
  DialogSystem.showQueued = showQueued;
  DialogSystem.hide = hide;
});

onUnmounted(() => {
  DialogSystem.addDialog = undefined;
  DialogSystem.removeDialog = undefined;
  DialogSystem.show = undefined;
  DialogSystem.showQueued = undefined;
  DialogSystem.hide = undefined;
});
</script>

<template>
  <div>
    <template v-for="[id, dialog] in dialogs" :key="id">
      <component
        :is="dialog.component"
        v-if="dialog.isInDom"
        v-bind="dialog.props"
        :ref="(el: any) => storeRef(el, dialog)"
      />
    </template>
  </div>
</template>
